import type { FastFieldConfig, FieldProps, FormikHandlers } from 'formik';
import { FastField } from 'formik';
import * as React from 'react';
import type { FormFieldValidate } from './validators';
import { readValidationErrorFromFormik } from './validators';

export interface FormFastFieldProps<T = string> extends Pick<FastFieldConfig<T>, 'shouldUpdate'> {
    name: string;
    children: (injectedProps: FormFastFieldInjectedProps<T>) => React.JSX.Element;
    validate?: FormFieldValidate<T>;
    validateFieldOnFieldChange?: boolean;
    validateFieldOnFieldBlur?: boolean;
    onAfterBlur?: (val: T) => void;
}

export interface FormFastFieldInjectedProps<T> {
    showError: boolean;
    formProps: FieldProps['form'];
    inputProps: {
        value: T;
        onChange: FormikHandlers['handleChange'];
        onBlur: React.InputHTMLAttributes<HTMLInputElement>['onBlur'];
        name: string;
    };
    errorMessage?: string | React.ReactNode | undefined;
}

/* Motivation:
    this is different from Formik "FastField" or "Field" because of how
    it works:
    1) validation here it is triggered only on field's own onBlur event by
    default. (It ignores Formik prop "validateOnBlur" that runs all validations
    on any onBlur event). This behavior can be changed by `validateFieldOn*` props
    2) inputProps.onChange, that is passed to children, takes VALUE argument (not event)
    
    CAUTION: validation itself doesn't force component to show error message
*/
export class FormFastField<T = string> extends React.PureComponent<FormFastFieldProps<T>> {
    static displayName = 'FormWizard.FastField';

    static defaultProps = {
        validateFieldOnFieldBlur: true,
        validateFieldOnFieldChange: false,
    };

    hasBeenChanged = false;

    render() {
        const {
            name,
            onAfterBlur,
            validate,
            validateFieldOnFieldBlur,
            validateFieldOnFieldChange,
            shouldUpdate,
            children,
        } = this.props;

        return (
            <FastField validate={validate} name={name} shouldUpdate={shouldUpdate}>
                {(fieldProps: FieldProps) => {
                    const { field: inputProps, form: formProps } = fieldProps;
                    const errorMessage = readValidationErrorFromFormik(
                        formProps.errors,
                        formProps.touched,
                        name,
                        formProps.submitCount
                    );
                    const handleBlur = (e: React.FocusEvent) => {
                        // Formik's FastField internal onBlur
                        inputProps.onBlur(e);

                        if (validate && validateFieldOnFieldBlur && this.hasBeenChanged) {
                            // // Logic specific for this component instance:
                            formProps.validateField(name);
                        }

                        if (onAfterBlur && this.hasBeenChanged) {
                            // Execute additional onBlur along with FastField internal onBlur
                            onAfterBlur && onAfterBlur(inputProps.value);
                        }
                    };

                    const handleChange = (fieldValue: any) => {
                        this.hasBeenChanged = true;
                        // Formik's FastField internal onChange
                        formProps.setFieldValue(name, fieldValue);
                        formProps.setFieldTouched(name, true);
                        // Logic specific for this component instance
                        validate && validateFieldOnFieldChange && formProps.validateField(name);
                    };

                    const augmentedProps = {
                        errorMessage,
                        formProps,
                        showError: !!errorMessage,
                        inputProps: {
                            ...inputProps,
                            onBlur: handleBlur,
                            onChange: handleChange,
                        },
                    };

                    return children(augmentedProps);
                }}
            </FastField>
        );
    }
}
